/**
 * DeleteConfirmDialog — Iter177/178.
 *
 * Universal "are you sure" modal for the 3 deletable entities
 * (appraisal, client, property). Operator picks Soft or Hard delete;
 * Hard requires typing the entity label as confirmation; the parent
 * does the actual fetch + onDeleted/onError callbacks.
 *
 * Backend contract (already wired in routes/_soft_delete.py):
 *   DELETE /api/{entity}s/{id}?mode=soft|hard&force=true|false
 *   POST   /api/{entity}s/{id}/restore
 *   GET    /api/{entity}s/{id}/dependents      ← clients + properties only
 *
 * Props:
 *   - kind: 'appraisal' | 'client' | 'property'
 *   - entityId: required
 *   - label: human-friendly name shown in the dialog (e.g. "A4821",
 *     "Test Company", "10 Test Place")
 *   - api: axios instance
 *   - onClose() / onDeleted({mode, id, response}) / onError(err)
 *   - showForceCheckbox: default true for client/property, false for
 *     appraisal (no dependent guard there).
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Trash2, Loader2, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ENTITY_PATH = {
  appraisal: 'appraisals',
  client:    'clients',
  property:  'properties',
};

export default function DeleteConfirmDialog({
  kind,
  entityId,
  label = '',
  api,
  onClose,
  onDeleted,
  onError,
}) {
  const path  = ENTITY_PATH[kind];
  const isApp = kind === 'appraisal';
  const needsForce = !isApp; // clients/properties have dependent guards

  const [mode, setMode] = useState('soft');      // 'soft' | 'hard'
  const [confirmText, setConfirmText] = useState('');
  const [force, setForce] = useState(false);
  // Iter182++ — cascade hard-deletes every linked appraisal +
  // property + their nested dependents in one shot. Default ON when
  // there ARE dependents (the safer choice — no orphans left behind).
  const [cascade, setCascade] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dependents, setDependents] = useState(null);

  // Iter178 — preflight dependent count for clients + properties so
  // the operator sees what they're about to break before pressing
  // delete. Skipped for appraisals (no inbound refs to count).
  useEffect(() => {
    if (isApp || !entityId) return;
    let alive = true;
    api.get(`/${path}/${entityId}/dependents`).then(({ data }) => {
      if (alive) setDependents(data);
    }).catch(() => { /* silent — proceed without preflight */ });
    return () => { alive = false; };
  }, [api, isApp, path, entityId]);

  const hardConfirmed =
    mode !== 'hard' ||
    confirmText.trim().toLowerCase() === (label || kind).trim().toLowerCase();

  const hasActive = !!dependents && (
    (dependents.active?.appraisals || 0) > 0 ||
    (dependents.active?.properties || 0) > 0
  );

  // For clients / properties with active dependents and force NOT
  // ticked, the button is locked.
  const blockedByDependents = needsForce && hasActive && !force && !cascade;

  const submit = async () => {
    setBusy(true);
    try {
      const params = [`mode=${mode}`];
      // Iter182++ — cascade takes priority over force; the backend
      // ignores `force` when cascade is set, but we send only one to
      // keep the audit log clean.
      if (needsForce && mode === 'hard' && cascade && hasActive) {
        params.push('cascade=true');
      } else if (needsForce && force) {
        params.push('force=true');
      }
      const { data } = await api.delete(`/${path}/${entityId}?${params.join('&')}`);
      const casc = data?.cascade_summary;
      let msg = `${kind[0].toUpperCase()}${kind.slice(1)} ${mode}-deleted`;
      if (casc) {
        const parts = [];
        if (casc.appraisals_deleted) parts.push(`${casc.appraisals_deleted} appraisal${casc.appraisals_deleted === 1 ? '' : 's'}`);
        if (casc.properties_deleted) parts.push(`${casc.properties_deleted} propert${casc.properties_deleted === 1 ? 'y' : 'ies'}`);
        if (parts.length) msg += ` (cascade: ${parts.join(' + ')})`;
      }
      toast.success(msg);
      onDeleted?.({ mode, id: entityId, response: data });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && typeof detail === 'object') {
        toast.error(detail.message || 'Has dependents — tick "force" to override');
        // Hydrate the dependents block in-place so operator sees why
        if (detail.dependents) setDependents({ active: detail.dependents, total: detail.dependents });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Delete failed');
        onError?.(e);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent data-testid={`delete-confirm-${kind}`} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Trash2 size={16} className="text-rose-600" /> Delete {kind}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-xs">
            <div className="text-slate-500">You're about to delete:</div>
            <div className="font-semibold text-slate-800 mt-0.5">{label || '(no label)'}</div>
            <div className="text-[10px] font-mono-num text-slate-400 mt-0.5">id: {entityId}</div>
          </div>

          {/* Mode picker */}
          <div className="space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mode</div>
            <label className="flex items-start gap-2 px-2 py-1.5 border rounded cursor-pointer hover:bg-slate-50">
              <input
                type="radio"
                name={`delete-mode-${kind}`}
                value="soft"
                checked={mode === 'soft'}
                onChange={() => setMode('soft')}
                data-testid={`delete-mode-soft-${kind}`}
              />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-[12px]">Soft delete <span className="font-normal text-slate-500">(recommended)</span></div>
                <div className="text-[10px] text-slate-500">Record is hidden from lists but kept in the database. Restore any time.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 px-2 py-1.5 border rounded cursor-pointer hover:bg-rose-50 border-rose-100">
              <input
                type="radio"
                name={`delete-mode-${kind}`}
                value="hard"
                checked={mode === 'hard'}
                onChange={() => setMode('hard')}
                data-testid={`delete-mode-hard-${kind}`}
              />
              <div className="flex-1">
                <div className="font-semibold text-rose-700 text-[12px]">Hard delete <span className="font-normal text-rose-600/70">(irreversible)</span></div>
                <div className="text-[10px] text-rose-600/80">
                  Permanently removes the {kind} {isApp ? 'AND every dependent record (quote items, photos, components, …)' : '.'}
                </div>
              </div>
            </label>
          </div>

          {/* Dependent counts (clients + properties) */}
          {needsForce && dependents && (
            <div data-testid={`delete-dependents-${kind}`} className={`px-2.5 py-2 border rounded text-[11px] ${
              hasActive ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}>
              <div className="flex items-center gap-1.5 font-semibold">
                {hasActive
                  ? <><AlertTriangle size={11} className="text-amber-600" /> Linked records found</>
                  : <>No active linked records</>
                }
              </div>
              <div className="mt-1 space-y-0.5">
                {kind === 'client' && (
                  <>
                    <div>Active appraisals: <strong>{dependents.active?.appraisals ?? 0}</strong> · Active properties: <strong>{dependents.active?.properties ?? 0}</strong></div>
                    <div className="text-[10px] opacity-70">Including soft-deleted — appraisals: {dependents.total?.appraisals ?? 0} · properties: {dependents.total?.properties ?? 0}</div>
                  </>
                )}
                {kind === 'property' && (
                  <>
                    <div>Active appraisals: <strong>{dependents.active?.appraisals ?? 0}</strong> · Roof details: <strong>{dependents.active?.roof_details ?? 0}</strong></div>
                    <div className="text-[10px] opacity-70">Including soft-deleted appraisals: {dependents.total?.appraisals ?? 0}</div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Iter182++ — Cascade vs Orphan picker. When there ARE
              dependents AND mode is hard, the operator MUST choose one.
              Cascade is the recommended default (no orphans left).
              "Force" is preserved for explicit override (e.g. moving
              a property between owners). */}
          {needsForce && hasActive && mode === 'hard' && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
                <AlertTriangle size={11} /> Linked records — pick one
              </div>
              <label className={`flex items-start gap-2 px-2 py-1.5 border rounded cursor-pointer text-[11px] ${cascade ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                <input
                  type="radio"
                  name={`delete-cascade-${kind}`}
                  checked={cascade}
                  onChange={() => { setCascade(true); setForce(false); }}
                  data-testid={`delete-cascade-${kind}`}
                  className="mt-0.5"
                />
                <span className="text-slate-800">
                  <strong className="text-emerald-800">Cascade delete (recommended)</strong> — also permanently remove every linked appraisal, property, photo and dependent. Leaves no orphans behind.
                </span>
              </label>
              <label className={`flex items-start gap-2 px-2 py-1.5 border rounded cursor-pointer text-[11px] ${force ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                <input
                  type="radio"
                  name={`delete-cascade-${kind}`}
                  checked={force}
                  onChange={() => { setForce(true); setCascade(false); }}
                  data-testid={`delete-force-${kind}`}
                  className="mt-0.5"
                />
                <span className="text-slate-800">
                  <strong className="text-rose-800">Orphan instead</strong> — delete only the {kind}; leave dependents in the DB with broken links. (Use Admin → Orphan Sweep to clean them later.)
                </span>
              </label>
            </div>
          )}
          {/* Legacy soft-delete force checkbox (kept for soft mode) */}
          {needsForce && hasActive && mode === 'soft' && (
            <label className="flex items-start gap-2 px-2 py-1.5 border border-rose-200 rounded bg-rose-50/40 cursor-pointer text-[11px]">
              <input
                type="checkbox"
                checked={force}
                onChange={e => setForce(e.target.checked)}
                data-testid={`delete-force-soft-${kind}`}
                className="mt-0.5"
              />
              <span className="text-rose-800">
                <strong>Force soft-delete despite linked records.</strong> Dependents will be left untouched.
              </span>
            </label>
          )}

          {/* Type-to-confirm for hard delete */}
          {mode === 'hard' && (
            <div className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
                <ShieldAlert size={11} /> Type <em>{label || kind}</em> to confirm
              </div>
              <input
                data-testid={`delete-confirm-input-${kind}`}
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={label || kind}
                className="w-full text-sm px-2 py-1.5 border border-rose-300 rounded focus:ring-1 focus:ring-rose-400"
                autoFocus
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            size="sm"
            variant={mode === 'hard' ? 'destructive' : 'default'}
            disabled={busy || !hardConfirmed || blockedByDependents}
            onClick={submit}
            data-testid={`delete-submit-${kind}`}
            className={mode === 'hard' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
          >
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Trash2 size={12} className="mr-1" />}
            {mode === 'hard' ? 'Hard delete' : 'Soft delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
